import React, { useEffect } from 'react';
import {
  Button,
  Select,
  Text,
  Group,
  LoadingOverlay,
  rem,
  CloseButton,
} from '@mantine/core';
import { createFormContext, zodResolver } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { getFetcher, postFetcher } from '@/api/fetchers';
import { IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { isEmptyForm } from '@/utils/functions';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { type Services } from '@/schemas/API/GET/service/response';
import {
  type CreateClient,
  createClientSchema,
  defaultClient,
} from '@/schemas/API/POST/client/request';
import { displayError } from '@/utils/error/errorHandler';
import { type ClientDetailed } from '@/schemas/API/GET/client/response';
import { PersonInfoCard } from './Cards/PersonInfoCard';
import { AddressCard } from './Cards/AddressCard';
import { ContactPersonCard } from './Cards/ContactPersonCard';
import { ContactCard } from './Cards/ContactCard';

export const [ClientFormProvider, useClientFormContext, useClientForm] =
  createFormContext<CreateClient>();

export const ClientFormPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: availableServices } = useSWR<Services>('services', getFetcher);

  const form = useClientForm({
    validate: zodResolver(createClientSchema),
    validateInputOnChange: true,
  });

  useEffect(() => {
    form.setInitialValues(defaultClient);
    form.setValues(defaultClient);
  }, []);

  const handleCancel = (): void => {
    if (!form.isDirty()) {
      navigate('/clients');
      return;
    }
    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete formulář
          uzavřít?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onConfirm: () => navigate('/clients'),
    });
  };

  const handleSave = async (): Promise<void> => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Formulář se nepodařilo uložit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      const { applicantData, serviceId, ...clientData } = form.values;

      const client: ClientDetailed = await postFetcher('/clients', {
        ...clientData,
        serviceId,
      });

      const carePlanData = {
        serviceId,
        applicantId: null,
        healthDesc: applicantData.statusDesc,
        note: applicantData.note ?? undefined,
        lastRevDate: dayjs().format('YYYY-MM-DD'),
        nextRevDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
        expDate: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      };

      await postFetcher(`/clients/${client.id}/careplans`, carePlanData);
      navigate(`/clients/${client.id}/personal`);
    } catch (error) {
      displayError(error);
    }
  };

  if (!isEmptyForm(form)) {
    return (
      <ClientFormProvider form={form}>
        <div className="p-8">
          <div className="flex flex-col sm:rounded-card sm:border sm:border-card sm:p-2.5 gap-2.5">
            <Group justify="space-between">
              <Text fw={700} size="xl">
                Formulář klienta
              </Text>
              <CloseButton onClick={handleCancel} />
            </Group>

            <Group justify="space-between">
              <Select
                label="Služba"
                onChange={e => {
                  form.setFieldValue('serviceId', parseInt(e ?? '0'));
                }}
                data={availableServices?.map(service => ({
                  value: service.id.toString(),
                  label: service.name,
                }))}
                defaultValue={availableServices ? availableServices[0].id.toString() : null}
                allowDeselect={false}
                error={form.getInputProps('serviceId').error}
              />
            </Group>

            <PersonInfoCard title="Klient" formContext={useClientFormContext} />

            <ContactCard formContext={useClientFormContext} disabled={false} />

            <Group justify="space-between" grow className="flex items-start">
              <AddressCard
                addressType="Trvalá adresa"
                field="permAddress"
                formContext={useClientFormContext}
              />
              <AddressCard
                addressType="Adresa poskytování"
                field="servAddress"
                formContext={useClientFormContext}
              />
            </Group>

            <ContactPersonCard
              title="Kontaktující"
              formContext={useClientFormContext}
            />

            <Group justify="flex-end">
              <Button variant="outline" onClick={handleCancel}>
                Zrušit
              </Button>

              <Button className="w-28" onClick={handleSave}>
                Uložit
              </Button>
            </Group>
          </div>
        </div>
      </ClientFormProvider>
    );
  }

  return (
    <div className="relative" style={{ height: '100%', width: '100%' }}>
      <LoadingOverlay
        visible
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    </div>
  );
};
